import { Box, Typography, Button, Divider, Link, IconButton, EmailOutlinedIcon, UserFlag,
  Instagram, UserAvatar, Stack, Loader, EditIcon, SettingsIcon, ExplorerPlusButtons, UserSocialMediaLinks,
  SVGIcon, StorefrontOutlinedIcon, ExplorerPlusIcon
} from 'src/components';
import { useAuth } from 'src/utils';
import '../../profile.css';
import { useIsMobile } from 'src/hooks';
import { getTransportMode, getUserMembershipLabel } from 'src/data';
import OnboardingContainer from '../onboarding/onboarding-container';
import { useNavigate } from 'react-router-dom';

export default function AccountSummary({ scrollRef, handleCurrentViewChange }) {
  const { user, isFetching } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const font = isMobile ? "body2" : "body3";
  const transportMode = getTransportMode(user?.transportMode);

  const isMembershipsEnabled = import.meta.env.VITE_FEATURE_MEMBERSHIPS === 'true';

  if (isFetching) {
    return <Loader />;
  }

  if (user && user.hasCompletedOnboarding === false) {
    return <OnboardingContainer scrollRef={scrollRef} handleCurrentViewChange={handleCurrentViewChange}/>;
  }

  const getFriendsMessage = () => {
    const friendCount = user?.friends?.length;
    if (friendCount == 0) {
      return "Following no explorers yet";
    } else if(friendCount == 1){
      return "Following 1 explorer";
    } else{
      return `Following ${friendCount} explorers`;
      }
  };

  return (
    <Box sx={{ width: '100%' }}>

      <Box sx={{display: 'flex', marginBottom: '10px'}}>
        <UserAvatar 
          user={user} 
          size={70} 
          isMobile={false}
          retryImage={true}
          retryImagePlaceholderDefault={true}
          retryImageShowLoader={true}
          showOfflineBadge={true} />
          
        <Box sx={{marginLeft: '15px', display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {user?.name}
          </Typography>
          <Typography variant={font} fontWeight="bold" color="text.secondary" >
            {user?.email}
          </Typography>
          <Typography variant={font} fontWeight="bold" color="text.secondary" >
            {getFriendsMessage()}
          </Typography>
        </Box>
      </Box>

      <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
        <Button 
          variant="outlined" 
          size="small"
          startIcon={<EditIcon />} 
          sx={{ borderRadius: 100, height: '25px', width: '45%'}}
          onClick={() => handleCurrentViewChange("EditProfile")}>
            <Typography variant={font} fontWeight="bold">
              Edit Profile
            </Typography>
        </Button>
        <Button 
          variant="outlined" 
          size="small"
          startIcon={<SettingsIcon />} 
          sx={{ borderRadius: 100, height: '25px', width: '45%' }}
          onClick={() => handleCurrentViewChange("AccountSettings")}>
            <Typography variant={font} fontWeight="bold">
              Settings
            </Typography>
        </Button>
      </Stack>

      <Divider sx={{ mb: 1, mt: 1.5 }} />

      <Typography variant={"body2"} fontWeight="bold" gutterBottom>
        Bio: <br />
      </Typography>
      <Typography variant={"body2"}>
        {user?.bio ?? "Tell us a bit about yourself, so others can get to know you."}
      </Typography>

      <Divider sx={{ mb: 2, mt: 1.5 }}/>

      <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}
        >
          <Typography variant={font} fontWeight="bold">
            Social Media
          </Typography>

          <UserSocialMediaLinks ig={user?.instagramHandle} links={user?.socialMediaLinks} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}
      >
        <Typography variant={font} fontWeight="bold">
          Location Privacy
        </Typography>
        <Typography variant={font} color="text.secondary">
          {user?.privacy}
        </Typography>
      </Box>

      <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}
        >
          <Typography variant={font} fontWeight="bold">
            Country
          </Typography>
          <div style={{display: 'flex', flexDirection:'row', alignItems: 'center'}}>
            <Typography variant={font} color="text.secondary">
              {user?.countryIsoAlpha2 || 'n/a'}
            </Typography>
            {user?.countryIsoAlpha2 && (<UserFlag countryCode={user.countryIsoAlpha2} sx={{ml: 1}}/>)}
          </div>
      </Box>

      {isMembershipsEnabled && (
        <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}
          >
            <Typography variant={font} fontWeight="bold">
              Membership
            </Typography>

            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              {user?.membership !== "Free" && (
                <ExplorerPlusIcon 
                    tier={user?.membership.toLowerCase()}
                    size={20}
                    variant="animated"
                />
              )}
              <Typography variant={font} color="text.secondary">
                {getUserMembershipLabel(user?.membership)}
              </Typography>
            </Box>
        </Box>
      )}

      {user?.provider == "Email" ? 
        <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}
          >
            <Typography variant={font} fontWeight="bold">
              Email Login
            </Typography>
            <Link 
              aria-disabled 
              variant={font}
              fontWeight="bold"
              sx={{cursor: 'pointer', textDecoration: 'none'}}
              onClick={() => handleCurrentViewChange("ChangePassword")} >
                Change Password
            </Link>
        </Box>
      : <></>}

      <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}
        >
          <Typography variant={font} fontWeight="bold">
            Transport Mode
          </Typography>

          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Typography variant={font} color="text.secondary">
              {transportMode.label}
            </Typography>
            <SVGIcon icon={transportMode.icon} size={isMobile ? 40 : 40} />
          </Box>
      </Box>

      <ExplorerPlusButtons />

      <Divider sx={{ mt:2 }} />

      <Box sx={{ textAlign: 'center', mt: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} justifyContent="center">
          <IconButton 
            href="https://instagram.com/PanAm.Explorer" 
            target="_blank"
            color="primary"
          >
            <Instagram />
          </IconButton>
          <IconButton 
            color="primary"
            onClick={() => navigate('/contact-us')}
          >
            <EmailOutlinedIcon />
          </IconButton>
          <IconButton 
            href="storefront"
            target="_blank"
            color="primary"
            disabled
          >
            <StorefrontOutlinedIcon />
          </IconButton>
        </Stack>
        
        <Typography variant={font} color="text.secondary">
          © {new Date().getFullYear()} PanAmExplorer. All rights reserved. v2.3.4
        </Typography>
      </Box>
    </Box>
  );
}